/**
 * <FundingSource/> is a component which take source data and will render the bank logo, display name, amounts, etc. Is
 * currently being used heavily within <FundingOptionSelector/>
 */

import PropTypes from 'prop-types'

import React from 'react'
import { rtlConvertInline, css } from 'lib/paypalphrodite'
import styles from './funding-source.styles'
import {
  buildFundingSourceDisplayName,
  getPPCreditSpecialOfferText,
  getRelevantFundingSource,
  generateSourceLogoUrls,
  isPayPalBalanceOnlyOption,
  buildMultiCurrencyBalanceDisplayName,
  buildFundingSourceAccountType,
  isUnclaimedBalanceCIPRequired,
  getUnclaimedBalanceDisplayName,
  getUnclaimedBalanceDisplaySubText,
} from '../utils/funding-option-utils'
import { trackLinkWithData } from '../../lib/analytics/fpti'
import { isDw2Repaint } from 'experiments/dw2Repaint'
import { PP_CREDIT_PROMO_STATE } from '../../../../lib/constants'
import CashAdvanceFeesWarning from './cash-advance-fees-overpanel/cash-advance-fees-warning'
import { getCashAdvanceWarningContent } from './cash-advance-fees-overpanel/cash-advance-fees-util'
import classnames from 'classnames'
import content from 'pp-react-l10n'
import {
  Link as PPLink,
  Badge,
  BodyText,
  CaptionText,
  Checkbox,
  Radio,
  v2green400,
  v2blue400,
  V2WarningSmIcon,
  v2yellow600,
} from '@paypalcorp/pp-react'
import glamorous from 'glamorous'
import { css as glamorousCSS } from 'glamor'
let i18n = content('transfer/fundingSource')

const FundingOptionWrapper = glamorous.div({}, ({ disabled = false }) => {
  return {
    opacity: disabled ? '0.3' : '1',
    '& label': {
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'block',
    },
  }
})
const FundingOptionRadioLabel = glamorous.div({
  display: 'flex',
  alignItems: 'flex-start',
})

const WarningIconStyles = glamorousCSS({
  color: v2yellow600,
  position: 'absolute',
  right: 0,
  top: 0,
})

const CreditSpecialOfferInfoText = glamorous.div({
  '& a': {
    color: v2blue400,
    fontFamily: 'PayPalSansBig-Medium, Helvetica Neue, Arial, sans-serif',
  },
})

const PreferredCheckboxDiv = glamorous.div(
  {
    marginTop: '0.8rem',
    '& label': {
      marginLeft: '1rem',
    },
  },
  ({ shouldDisable = false }) => {
    return {
      opacity: shouldDisable ? '0.3' : '1',
      '& label': {
        cursor: shouldDisable ? 'not-allowed' : 'pointer',
      },
    }
  }
)

export class FundingSource extends React.Component {
  static propTypes = {
    fundingOption: PropTypes.shape({
      id: PropTypes.string.isRequired,
      feePaidByReceiver: PropTypes.bool,
      displayAmounts: PropTypes.object.isRequired,
      isFirstFeeFree: PropTypes.bool,
    }),
    togglePreferredLegalScrollOverpanel: PropTypes.func.isRequired,
    setPreferredId: PropTypes.string,
    handlePreferredCheckboxChange: PropTypes.func,
    isPurchase: PropTypes.bool,
    isSelected: PropTypes.bool,
    isRTL: PropTypes.bool,
    showCheaperOptionInfo: PropTypes.bool,
    disabled: PropTypes.bool,
    handleSelect: PropTypes.func,
    currencySeparator: PropTypes.string,
    getAnalyticsObject: PropTypes.func,
    index: PropTypes.number,
    isCFPBEnabledCountry: PropTypes.bool,
    userAccountType: PropTypes.string,
    isCardArtEnabled: PropTypes.bool,
    showPreferredCheckbox: PropTypes.bool,
    cashAdvanceWarningContent: PropTypes.object,
    warningLabelOnClick: PropTypes.func,
  }

  renderCheaperOptionWarning = () => {
    return (
      <div
        className={`vx_alert vx_alert-help ${css(styles.firstTimeInfoWrapper)}`}
      >
        <p className="vx_alert-text">{i18n('alert.cheaperAvailable')}</p>
      </div>
    )
  }

  renderFeeLine = () => {
    let {
      displayAmounts: { fee: feeAmount },
    } = this.props.fundingOption

    return isDw2Repaint() ? (
      <CaptionText data-nemo={`choice-FI-Fee-${this.props.index}`}>
        {i18n('fee', { feeAmount })}
      </CaptionText>
    ) : (
      <p
        data-nemo={`choice-FI-Fee-${this.props.index}`}
        className={css(styles.feeLine)}
      >
        {i18n('fee', { feeAmount })}
      </p>
    )
  }

  showFeeWaived = () => {
    return isDw2Repaint() ? (
      <BodyText
        strong
        data-nemo={`choice-FI-Fee-Waived-${this.props.index}`}
        style={{ color: v2green400 }}
      >
        {i18n('feeWaived')}
      </BodyText>
    ) : (
      <p
        data-nemo={`choice-FI-Fee-Waived-${this.props.index}`}
        className={`txtBold ${css(styles.feeLine, styles.feeWaived)}`}
      >
        {i18n('feeWaived')}
      </p>
    )
  }

  renderUnclaimedBalanceSubText = () => {
    return isDw2Repaint() ? (
      <CaptionText>{getUnclaimedBalanceDisplaySubText()}</CaptionText>
    ) : (
      <span className={`vx_text-6_lighter ${css(styles.radioButtonSubText)}`}>
        {getUnclaimedBalanceDisplaySubText()}
      </span>
    )
  }

  renderPreferredBadge = () => {
    return isDw2Repaint() ? (
      <Badge type="feature" style={{ marginTop: '0.5rem' }}>
        {i18n('badge.preferred')}
      </Badge>
    ) : (
      <div className={css(styles.preferredBadge)}>
        <p className={`vx_text-6_lighter ${css(styles.preferredBadgeText)}`}>
          {i18n('preferred')}
        </p>
      </div>
    )
  }

  renderAccountType = () => {
    const { fundingOption } = this.props
    if (
      !this.props.isCardArtEnabled ||
      isPayPalBalanceOnlyOption(fundingOption)
    ) {
      return null
    }
    const fundingSource = getRelevantFundingSource(fundingOption)
    const accountType = buildFundingSourceAccountType(fundingSource)
    const accountTypeClasses = classnames(
      'vx_text-body_secondary',
      css(styles.fundingSecondaryText, styles.preventIncorrectLabelWrapWithLogo)
    )
    return isDw2Repaint() ? (
      <CaptionText>{accountType}</CaptionText>
    ) : (
      <p className={accountTypeClasses}>{accountType}</p>
    )
  }

  renderPPCreditSpecialOfferInfo = () => {
    const { fundingOption } = this.props
    const fundingSource = getRelevantFundingSource(fundingOption)
    const creditSpecialOfferText = getPPCreditSpecialOfferText(fundingSource)
    const showOfferEligibilityText =
      fundingSource.ppcreditPromoFinancingOffer ===
        PP_CREDIT_PROMO_STATE.ELIGIBLE ||
      fundingSource.ppcreditPromoFinancingOffer ===
        PP_CREDIT_PROMO_STATE.INELIGIBLE
    const specialOfferTextClasses = classnames(
      'vx_text-body_secondary',
      css(
        styles.fundingSecondaryText,
        styles.preventIncorrectLabelWrapWithLogo,
        styles.ppcreditSpecialOfferText,
        showOfferEligibilityText && styles.offerEligibilityText
      )
    )

    return (
      creditSpecialOfferText &&
      (isDw2Repaint() ? (
        <CaptionText
          as={CreditSpecialOfferInfoText}
          {...creditSpecialOfferText}
        />
      ) : (
        <p className={specialOfferTextClasses}>
          <span {...creditSpecialOfferText} />
        </p>
      ))
    )
  }

  renderPreferredCheckbox = () => {
    const {
      fundingOption,
      setPreferredId,
      handlePreferredCheckboxChange,
      togglePreferredLegalScrollOverpanel,
    } = this.props
    const { id } = getRelevantFundingSource(fundingOption)
    const shouldDisable = setPreferredId && setPreferredId !== id

    return isDw2Repaint() ? (
      <PreferredCheckboxDiv
        shouldDisable={shouldDisable}
        tabIndex="0"
        role="button"
      >
        <Checkbox
          disabled={shouldDisable}
          onChange={() => handlePreferredCheckboxChange(id)}
          id="selection-instrument-preferred-checkbox"
          data-nemo="choice-select-checkbox"
          label={
            <div style={{ margin: '0 0.5rem' }}>
              <BodyText>{i18n('checkbox.setPreferred')}</BodyText>
              <PPLink
                size="sm"
                onClick={() => {
                  const analyticsData = this.props.getAnalyticsObject()
                  trackLinkWithData(
                    'learnmore',
                    'main:p2p:choice:fioption',
                    'main:p2p:choice:fioption::node',
                    analyticsData,
                    'link'
                  )
                  togglePreferredLegalScrollOverpanel()
                }}
              >
                {i18n('link.learnMore')}
              </PPLink>
            </div>
          }
        />
      </PreferredCheckboxDiv>
    ) : (
      <div
        role="button"
        className={`vx_checkbox ${css(
          styles.preferredCheckboxWrapper,
          shouldDisable && styles.greyedOut
        )}`}
        tabIndex="0"
      >
        <input
          disabled={shouldDisable}
          onChange={() => handlePreferredCheckboxChange(id)}
          type="checkbox"
          id="selection-instrument-preferred-checkbox"
        />
        <label
          className={css(
            rtlConvertInline(styles.checkboxOutline),
            rtlConvertInline(styles.preferredCheckbox),
            rtlConvertInline(styles.forceDisplayBlock),
            shouldDisable && styles.disabledLabel
          )}
          htmlFor="selection-instrument-preferred-checkbox"
          data-nemo="choice-select-checkbox"
        >
          <span className={css(styles.preventIncorrectLabelWrap)}>
            {i18n('checkbox.setPreferred')}
          </span>
        </label>
        <button
          onClick={() => {
            const analyticsData = this.props.getAnalyticsObject()
            trackLinkWithData(
              'learnmore',
              'main:p2p:choice:fioption',
              'main:p2p:choice:fioption::node',
              analyticsData,
              'link'
            )
            togglePreferredLegalScrollOverpanel()
          }}
          type="button"
          className={`vx_text-6_lighter vx_btn-link ${css(
            rtlConvertInline(styles.preferredInfoLink)
          )}`}
        >
          {i18n('link.learnMore')}
        </button>
      </div>
    )
  }

  /**
   * Helper function that enables the display of a warning icon when Cash Advance Fee applies
   * Or if a bank has the RTB Consent Contingency and Open Banking is enabled.
   * @param props
   * @param fundingSource
   * @returns {boolean}
   */
  shouldRenderWarningIcon = (props, fundingSource) => {
    const { isPurchase, isSelected } = props
    const { cashAdvanceFeeApplies } = fundingSource

    if (isSelected) {
      return false
    }

    return !isPurchase && cashAdvanceFeeApplies
  }

  render() {
    const {
      fundingOption,
      fundingOption: { id, feePaidByReceiver, isFirstFeeFree = false },
      isSelected,
      disabled,
      handleSelect,
      isPurchase,
      cashAdvanceWarningContent = '',
    } = this.props
    const showFeeLine = !feePaidByReceiver

    const fundingSource = getRelevantFundingSource(fundingOption)

    const {
      isPreferred,
      thumbnail_logo_url,
      cashAdvanceFeeApplies,
      generic_issuer_name,
    } = fundingSource
    const showPreferredCheckbox =
      isSelected &&
      !isPreferred &&
      this.props.showPreferredCheckbox &&
      !isUnclaimedBalanceCIPRequired(fundingOption)

    let title = ''
    if (isUnclaimedBalanceCIPRequired(fundingOption)) {
      // Set the Display Name Separately for No Balance
      title = getUnclaimedBalanceDisplayName(fundingSource)
    } else {
      title = isPayPalBalanceOnlyOption(fundingOption)
        ? buildMultiCurrencyBalanceDisplayName(
            fundingOption,
            this.props.isCFPBEnabledCountry,
            this.props.userAccountType
          )
        : buildFundingSourceDisplayName(
            fundingSource,
            this.props.isCFPBEnabledCountry,
            this.props.userAccountType,
            this.props.isCardArtEnabled
          )
    }

    const cardArtStyles =
      this.props.isCardArtEnabled && thumbnail_logo_url
        ? rtlConvertInline(styles.choiceCardArt)
        : rtlConvertInline(styles.fundingOptionLogo)
    const cardArtClasses = classnames(css(cardArtStyles))
    const radionBtnStyle = this.props.isRTL
      ? styles.radioButtonTextRTL
      : styles.radioButtonText

    return (
      <li style={{ position: 'relative' }}>
        {isDw2Repaint() ? (
          <FundingOptionWrapper disabled={disabled}>
            <Radio
              id={id}
              name="fundingOption"
              data-nemo={`choice-FI-selector-${this.props.index}`}
              tabIndex={isSelected ? 0 : -1}
              onChange={() => handleSelect(fundingOption)}
              value={id}
              checked={isSelected}
              disabled={disabled}
              aria-checked={isSelected}
              htmlFor={id}
              label={
                <>
                  <FundingOptionRadioLabel>
                    <img
                      alt=""
                      className={cardArtClasses}
                      src={generateSourceLogoUrls(
                        fundingSource,
                        this.props.isCFPBEnabledCountry,
                        this.props.userAccountType,
                        this.props.isCardArtEnabled
                      )}
                      width={50}
                    />
                    <div style={{ margin: '0 1rem' }}>
                      <BodyText
                        data-nemo={`choice-FI-Name-${this.props.index}`}
                      >
                        {title}
                        {isUnclaimedBalanceCIPRequired(fundingOption) &&
                          this.renderUnclaimedBalanceSubText()}
                      </BodyText>
                      {this.renderPPCreditSpecialOfferInfo()}
                      {this.renderAccountType()}
                      {isFirstFeeFree
                        ? this.showFeeWaived()
                        : showFeeLine && this.renderFeeLine()}
                      {isPreferred && this.renderPreferredBadge()}
                    </div>
                  </FundingOptionRadioLabel>
                  {isPurchase &&
                    showPreferredCheckbox &&
                    this.renderPreferredCheckbox()}
                  {!isPurchase && isSelected && cashAdvanceFeeApplies && (
                    <CashAdvanceFeesWarning
                      stylesOverride={{
                        marginTop: '12px',
                      }}
                      messageOverride={getCashAdvanceWarningContent(
                        cashAdvanceWarningContent,
                        generic_issuer_name
                      )}
                      handleOnClick={this.props.warningLabelOnClick}
                    />
                  )}
                  {this.shouldRenderWarningIcon(this.props, fundingSource) && (
                    <V2WarningSmIcon
                      size="sm"
                      className={WarningIconStyles.toString()}
                    />
                  )}
                </>
              }
            />
          </FundingOptionWrapper>
        ) : (
          <>
            <div
              className={css(
                rtlConvertInline(styles.fundingOptionWrapper),
                disabled && styles.greyedOut
              )}
            >
              <div
                className={`vx_radio ${rtlConvertInline(
                  css(styles.radioButtonWrapper)
                )}`}
              >
                <input
                  id={id}
                  name="fundingOption"
                  data-nemo={`choice-FI-selector-${this.props.index}`}
                  type="radio"
                  tabIndex={isSelected ? 0 : -1}
                  onChange={() => handleSelect(fundingOption)}
                  value={id}
                  checked={isSelected}
                  disabled={disabled}
                  aria-checked={isSelected}
                />
                <label
                  htmlFor={id}
                  className={css(
                    rtlConvertInline(styles.radioButton),
                    rtlConvertInline(styles.forceDisplayBlock),
                    disabled && rtlConvertInline(styles.disabledLabel)
                  )}
                >
                  <img
                    alt=""
                    className={cardArtClasses}
                    src={generateSourceLogoUrls(
                      fundingSource,
                      this.props.isCFPBEnabledCountry,
                      this.props.userAccountType,
                      this.props.isCardArtEnabled
                    )}
                    width={50}
                  />
                  <p
                    className={css(
                      rtlConvertInline(radionBtnStyle),
                      rtlConvertInline(styles.preventIncorrectLabelWrapWithLogo)
                    )}
                    data-nemo={`choice-FI-Name-${this.props.index}`}
                  >
                    {title}
                    {isUnclaimedBalanceCIPRequired(fundingOption) &&
                      this.renderUnclaimedBalanceSubText()}
                  </p>
                </label>
              </div>
              {this.renderPPCreditSpecialOfferInfo()}
              {this.renderAccountType()}
              {isFirstFeeFree
                ? this.showFeeWaived()
                : showFeeLine && this.renderFeeLine()}
              {isPreferred && this.renderPreferredBadge()}
              {isPurchase &&
                showPreferredCheckbox &&
                this.renderPreferredCheckbox()}

              {!isPurchase && isSelected && cashAdvanceFeeApplies && (
                <CashAdvanceFeesWarning
                  stylesOverride={{
                    margin: 'auto auto auto 2.75rem',
                    marginTop: `${isPreferred ? '1rem' : 'inherit'}`,
                    paddingRight: '1rem',
                  }}
                  messageOverride={getCashAdvanceWarningContent(
                    cashAdvanceWarningContent,
                    generic_issuer_name
                  )}
                  handleOnClick={this.props.warningLabelOnClick}
                />
              )}
            </div>
            {this.shouldRenderWarningIcon(this.props, fundingSource) && (
              <span
                style={{
                  position: 'absolute',
                  color: 'orange',
                  top: '28px',
                  right: '0',
                }}
                className="ppvx_icon ppvx_icon--size_xs ppvx_icon-warning-alt"
              />
            )}
          </>
        )}
        <hr className={css(rtlConvertInline(styles.lineSeparator))} />
      </li>
    )
  }
}

export default FundingSource
